import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { fetchVouchers } from '../../actions/action_vouchers';

const PageContainer = styled.div`
  margin-top: 0;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h4`
  margin: 0;
  font-size: 24px;
`;

const BackLink = styled(Link)`
  color: #479de6;
  text-decoration: none;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
  }
`;

const SummaryCard = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 24px;
  color: #333;
  margin-bottom: 24px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalBalance = styled.div`
  font-size: 36px;
  font-weight: bold;
  color: #16a34a;
`;

const BalanceLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const BalanceBreakdown = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #bbf7d0;
`;

const BreakdownItem = styled.div`
  text-align: center;
`;

const BreakdownValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #16a34a;
`;

const BreakdownLabel = styled.div`
  font-size: 12px;
  color: #666;
`;

const VoucherList = styled.div`
  margin-top: 24px;
`;

const SectionTitle = styled.h5`
  font-size: 18px;
  color: #333;
  margin-bottom: 16px;
`;

const VoucherCard = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  background: #fff;
`;

const VoucherHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const VoucherCode = styled.code`
  font-size: 16px;
  background: #e8f5e9;
  padding: 4px 10px;
  border-radius: 4px;
  color: #2e7d32;
  font-weight: 600;
`;

const VoucherHeaderRight = styled.div`
  text-align: right;
`;

const VoucherProduct = styled.div`
  font-size: 13px;
  color: #333;
  font-weight: 500;
`;

const VoucherDate = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 2px;
`;

const VoucherBalance = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #22c55e;
  margin-bottom: 8px;
`;

const BalanceBreakdownList = styled.div`
  margin-left: 8px;
  font-size: 13px;
  color: #666;
`;

const BalanceItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;

  &:before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 1px;
    background: #ccc;
    margin-right: 8px;
  }
`;

const PromoExpiry = styled.span`
  color: #92400e;
  font-size: 12px;
  margin-left: 4px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    if (props.status === 'active') return '#dcfce7';
    if (props.status === 'depleted') return '#f3f4f6';
    return '#fef3c7';
  }};
  color: ${(props) => {
    if (props.status === 'active') return '#166534';
    if (props.status === 'depleted') return '#6b7280';
    return '#92400e';
  }};
  margin-left: 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const SecondaryButton = styled(Link)`
  flex: 1;
  background-color: #479de6;
  color: #ffffff;
  border: none;
  padding: 14px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  text-align: center;
  text-decoration: none;
  &:hover {
    background-color: #3b8bd4;
    color: #ffffff;
  }
`;

const PrimaryButton = styled(Link)`
  flex: 1;
  background-color: #22c55e;
  color: #ffffff;
  border: none;
  padding: 14px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  text-align: center;
  text-decoration: none;
  &:hover {
    background-color: #16a34a;
    color: #ffffff;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (cents) => {
  if (!cents && cents !== 0) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

const MyVouchersPage = () => {
  const dispatch = useDispatch();
  const vouchers = useSelector((state) => state.vouchers.vouchers);
  const vouchersLoading = useSelector(
    (state) => state.vouchers.vouchersLoading,
  );

  useEffect(() => {
    dispatch(fetchVouchers());
  }, [dispatch]);

  // Calculate totals from vouchers
  const totalBalance = vouchers.reduce(
    (sum, v) => sum + (v.total_balance_cents || 0),
    0,
  );
  const purchasedBalance = vouchers.reduce(
    (sum, v) => sum + (v.purchased_balance_cents || 0),
    0,
  );
  const promoBalance = vouchers.reduce(
    (sum, v) => sum + (v.promo_balance_cents || 0),
    0,
  );

  // Separate active and depleted vouchers
  const activeVouchers = vouchers.filter((v) => v.status === 'active');
  const depletedVouchers = vouchers.filter((v) => v.status === 'depleted');

  if (vouchersLoading) {
    return (
      <PageContainer>
        <p>Loading vouchers...</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>My Vouchers</PageTitle>
        <BackLink to="/my-account">&larr; Back to Account</BackLink>
      </PageHeader>

      <SummaryCard>
        <SummaryRow>
          <div>
            <BalanceLabel>Total Available Balance</BalanceLabel>
            <TotalBalance>{formatCurrency(totalBalance)}</TotalBalance>
          </div>
        </SummaryRow>
        {(purchasedBalance > 0 || promoBalance > 0) && (
          <BalanceBreakdown>
            <BreakdownItem>
              <BreakdownValue>
                {formatCurrency(purchasedBalance)}
              </BreakdownValue>
              <BreakdownLabel>Purchased Credit</BreakdownLabel>
            </BreakdownItem>
            <BreakdownItem>
              <BreakdownValue>{formatCurrency(promoBalance)}</BreakdownValue>
              <BreakdownLabel>Promo Credit</BreakdownLabel>
            </BreakdownItem>
          </BalanceBreakdown>
        )}
      </SummaryCard>

      {vouchers.length === 0 ? (
        <EmptyState>
          <p>You don&apos;t have any vouchers yet.</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>
            Purchase a voucher to get bonus credit on your account!
          </p>
        </EmptyState>
      ) : (
        <>
          {activeVouchers.length > 0 && (
            <VoucherList>
              <SectionTitle>
                Active Vouchers ({activeVouchers.length})
              </SectionTitle>
              {activeVouchers.map((voucher) => (
                <VoucherCard key={voucher.id}>
                  <VoucherHeader>
                    <div>
                      <VoucherCode>{voucher.code}</VoucherCode>
                      <StatusBadge status={voucher.status}>
                        {voucher.status}
                      </StatusBadge>
                    </div>
                    <VoucherHeaderRight>
                      <VoucherProduct>{voucher.product_name}</VoucherProduct>
                      <VoucherDate>
                        Purchased {formatDate(voucher.purchased_at)}
                      </VoucherDate>
                    </VoucherHeaderRight>
                  </VoucherHeader>
                  <VoucherBalance>
                    {voucher.total_balance_formatted} available
                  </VoucherBalance>
                  <BalanceBreakdownList>
                    <BalanceItem>
                      {voucher.purchased_balance_formatted} purchased credit
                    </BalanceItem>
                    <BalanceItem>
                      {voucher.promo_balance_formatted} promo credit
                      {voucher.promo_balance_cents > 0 &&
                        voucher.promo_expires_at &&
                        !voucher.promo_expired && (
                          <PromoExpiry>
                            (expires {formatDate(voucher.promo_expires_at)})
                          </PromoExpiry>
                        )}
                      {voucher.promo_expired && (
                        <PromoExpiry style={{ color: '#dc2626' }}>
                          (expired)
                        </PromoExpiry>
                      )}
                    </BalanceItem>
                  </BalanceBreakdownList>
                </VoucherCard>
              ))}
            </VoucherList>
          )}

          {depletedVouchers.length > 0 && (
            <VoucherList>
              <SectionTitle style={{ color: '#999' }}>
                Used Vouchers ({depletedVouchers.length})
              </SectionTitle>
              {depletedVouchers.map((voucher) => (
                <VoucherCard key={voucher.id} style={{ opacity: 0.7 }}>
                  <VoucherHeader>
                    <div>
                      <VoucherCode
                        style={{ background: '#f3f4f6', color: '#6b7280' }}
                      >
                        {voucher.code}
                      </VoucherCode>
                      <StatusBadge status={voucher.status}>
                        {voucher.status}
                      </StatusBadge>
                    </div>
                    <VoucherHeaderRight>
                      <VoucherProduct>{voucher.product_name}</VoucherProduct>
                      <VoucherDate>
                        Purchased {formatDate(voucher.purchased_at)}
                      </VoucherDate>
                    </VoucherHeaderRight>
                  </VoucherHeader>
                  <VoucherBalance style={{ color: '#9ca3af' }}>
                    $0.00 available
                  </VoucherBalance>
                  {voucher.last_used_at && (
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      Last used {formatDate(voucher.last_used_at)}
                    </div>
                  )}
                </VoucherCard>
              ))}
            </VoucherList>
          )}
        </>
      )}

      <ButtonRow>
        <SecondaryButton to="/dashboard">Start an Order</SecondaryButton>
        {/* TODO: Re-enable when voucher purchase flow is ready for direct purchase
        <PrimaryButton to="/vouchers/purchase">Buy More Vouchers</PrimaryButton>
        */}
      </ButtonRow>
    </PageContainer>
  );
};

export default MyVouchersPage;
